/**
 * PUBLIC gate-pass page — usebero.com/p/<code>. No login. A resident shares
 * this link; the guest opens it in any browser and shows the QR/PIN at the
 * gate. Delivers the ticket's promise: "No app needed for your guest."
 * Always live: a cancelled/expired pass shows that state here too.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Text as RNText, View } from 'react-native';

import { palette, radius, space } from '@bero/tokens';
import { QRBox } from '@/components/gate/bits';
import { useT, type TFunc } from '@/i18n';
import { AppText, Screen } from '@/ui';

const BRAND = palette.light;
const FIG = palette.dark.accent500;

/** Same fixed dark-lacquer badge as the in-app ticket — one shared object. */
const TICKET = {
  paper: palette.light.primary900,
  rule: palette.light.primary700,
  ink: palette.light.canvas0,
  inkMuted: palette.light.primary100,
  accent: palette.dark.accent500,
};

function GuestRail({ word = 'GUEST' }: { word?: string }) {
  const [railH, setRailH] = React.useState(0);
  return (
    <View
      onLayout={(e) => setRailH(e.nativeEvent.layout.height)}
      style={{ width: 56, alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center' }}
    >
      {railH > 0 ? (
        <RNText
          numberOfLines={1}
          style={{
            position: 'absolute', width: railH, minWidth: railH, maxWidth: railH, height: 48, textAlign: 'center',
            left: (56 - railH) / 2, top: railH / 2 - 24,
            transform: [{ rotate: '-90deg' }],
            fontFamily: 'Sora_800ExtraBold', fontSize: 34, lineHeight: 48,
            letterSpacing: 10, color: TICKET.ink, opacity: 0.95,
          }}
        >
          {word}
        </RNText>
      ) : null}
    </View>
  );
}

type PublicPass = {
  visitorName: string; pin: string; token: string;
  validFrom?: string; validUntil?: string; multiUse: boolean; dailyWindow?: string;
  status: 'active' | 'upcoming' | 'expired' | 'cancelled';
  society: string; hostName: string; flatCode: string;
};

function Mark({ height = 20 }: { height?: number }) {
  const u = height / 58;
  return (
    <View style={{ width: 50 * u, height }}>
      <View style={{ position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u, borderWidth: 9 * u, borderBottomWidth: 0, borderColor: BRAND.canvas0, borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u }} />
      <View style={{ position: 'absolute', left: 19 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: FIG }} />
      <View style={{ position: 'absolute', left: 20 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: FIG }} />
    </View>
  );
}

// t() is passed in (reactive) — dates stay in English (leave), only the
// 'valid until' / entry-type labels localise.
function validText(p: PublicPass, t: TFunc): string {
  const use = p.multiUse ? t('pp_multi_entry') : t('pp_single_entry');
  const u = p.validUntil ? new Date(p.validUntil) : null;
  if (u && !Number.isNaN(u.getTime())) {
    let h = u.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const time = `${h}:${String(u.getMinutes()).padStart(2, '0')} ${am}`;
    return `${u.getDate()} ${MON[u.getMonth()]} · ${t('pp_valid_until', { time })} · ${use}`;
  }
  return use;
}

/** Tonal pills on the dark paper — status reads in cream, never neon.
 * labelKey resolves reactively via the hook t() at render. */
const STATUS = {
  active: { bg: TICKET.rule, fg: TICKET.ink, labelKey: 'pp_status_active' },
  upcoming: { bg: TICKET.rule, fg: TICKET.inkMuted, labelKey: 'pp_status_upcoming' },
  expired: { bg: TICKET.rule, fg: TICKET.inkMuted, labelKey: 'pp_status_expired' },
  cancelled: { bg: BRAND.danger700, fg: TICKET.ink, labelKey: 'pp_status_cancelled' },
} as const;

export default function PublicPass() {
  const { code } = useLocalSearchParams<{ code: string }>();
  const { t } = useT();
  const [pass, setPass] = React.useState<PublicPass | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'notfound'>('loading');

  React.useEffect(() => {
    if (!code) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/visitors/pass/${code}`);
        if (!res.ok) { if (!cancelled) setState('notfound'); return; }
        const data = (await res.json()) as PublicPass;
        if (!cancelled) { setPass(data); setState('ready'); }
      } catch {
        if (!cancelled) setState('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.canvas1 }}>
      <Screen padded safeTop style={{ paddingTop: space.s8, alignItems: 'center' }}>
        {state === 'loading' ? (
          <AppText tone="tertiary" style={{ marginTop: space.s10 }}>{t('pp_loading')}</AppText>
        ) : state === 'notfound' || !pass ? (
          <View style={{ alignItems: 'center', marginTop: space.s10, gap: space.s3 }}>
            <Mark height={40} />
            <AppText variant="headingSm" weight="600">{t('pp_invalid_title')}</AppText>
            <AppText variant="bodyMd" tone="secondary" style={{ textAlign: 'center', maxWidth: 300 }}>
              {t('pp_invalid_body')}
            </AppText>
          </View>
        ) : (
          <>
            <View style={{
              width: '100%', maxWidth: 360, alignSelf: 'center',
              backgroundColor: TICKET.paper, borderRadius: radius.xxl, overflow: 'hidden',
            }}>
              {/* brand row on the paper itself */}
              <View style={{ paddingTop: space.s5, paddingHorizontal: space.s5, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Mark height={20} />
                <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: 16, letterSpacing: -0.64, color: TICKET.ink }}>bero</RNText>
                <AppText variant="label" style={{ marginLeft: 'auto', color: TICKET.inkMuted, opacity: 0.8 }} numberOfLines={1}>{pass.society}</AppText>
              </View>

              {/* rail | rule | content */}
              <View style={{ flexDirection: 'row', paddingVertical: space.s5, paddingRight: space.s5 }}>
                <GuestRail />
                <View style={{ width: 1, backgroundColor: TICKET.rule, marginRight: space.s5 }} />
                <View style={{ flex: 1, alignItems: 'center' }}>
                  <RNText
                    numberOfLines={2}
                    style={{
                      fontFamily: 'Sora_800ExtraBold', fontSize: 22, lineHeight: 28,
                      letterSpacing: -0.4, color: TICKET.ink, textAlign: 'center', alignSelf: 'stretch',
                    }}
                  >
                    {pass.visitorName}
                  </RNText>
                  <View style={{ backgroundColor: STATUS[pass.status].bg, borderRadius: radius.full, paddingHorizontal: space.s3, paddingVertical: 4, marginTop: space.s2 }}>
                    <AppText variant="label" style={{ color: STATUS[pass.status].fg }}>{t(STATUS[pass.status].labelKey)}</AppText>
                  </View>
                  <View style={{ marginTop: space.s4, borderRadius: radius.lg }}>
                    <QRBox size={150} seed={`BERO-${pass.token}`} />
                  </View>
                  <AppText variant="label" style={{ marginTop: space.s4, color: TICKET.inkMuted, opacity: 0.75 }}>{t('pp_or_tell_guard')}</AppText>
                  <AppText mono weight="600" numberOfLines={1} style={{ fontSize: 30, lineHeight: 38, letterSpacing: 8, marginTop: 4, textAlign: 'center', color: TICKET.accent }}>
                    {pass.pin}
                  </AppText>
                </View>
              </View>

              <View style={{ height: 1, backgroundColor: TICKET.rule, marginHorizontal: space.s5 }} />

              <View style={{ padding: space.s5, gap: space.s2 }}>
                <Row label={t('pp_visiting')} value={`${pass.hostName} · ${pass.flatCode}`} />
                <Row label={t('pp_valid_label')} value={validText(pass, t)} />
                {pass.dailyWindow ? <Row label={t('pp_daily')} value={pass.dailyWindow} /> : null}
              </View>
            </View>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s5, textAlign: 'center', maxWidth: 340 }}>
              {t('pp_footer')}
            </AppText>
          </>
        )}
      </Screen>
    </View>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s4 }}>
      <AppText variant="bodySm" style={{ flexShrink: 0, paddingTop: 1, color: TICKET.inkMuted, opacity: 0.75 }}>{label}</AppText>
      <AppText variant="bodySm" weight="600" style={{ flex: 1, textAlign: 'right', color: TICKET.ink }}>{value}</AppText>
    </View>
  );
}
